export interface Vec2 {
  x: number;
  y: number;
}
export interface Ray {
  origin: Vec2;
  dir: Vec2;
}
export interface Segment {
  a: Vec2;
  b: Vec2;
}
export interface Hit {
  point: Vec2;
  t: number;
}

const EPS = 1e-6;

/** Cast a ray against a finite segment. Returns hit point + ray-parameter t (≥ EPS). */
export function rayHitSegment(ray: Ray, seg: Segment): Hit | null {
  // Parametric: ray(t) = origin + t * dir, t ≥ 0
  //            seg(s) = a + s * (b - a), 0 ≤ s ≤ 1
  // Solve for t, s.
  const r = ray.dir;
  const sx = seg.b.x - seg.a.x;
  const sy = seg.b.y - seg.a.y;
  const denom = r.x * sy - r.y * sx;
  if (Math.abs(denom) < EPS) return null; // parallel

  const dx = seg.a.x - ray.origin.x;
  const dy = seg.a.y - ray.origin.y;
  const t = (dx * sy - dy * sx) / denom;
  const s = (dx * r.y - dy * r.x) / denom;
  if (t < EPS) return null;
  if (s < 0 || s > 1) return null;

  return {
    point: { x: ray.origin.x + r.x * t, y: ray.origin.y + r.y * t },
    t,
  };
}

/** Reflect a direction vector around a (unit-length) normal. */
export function reflect(dir: Vec2, normal: Vec2): Vec2 {
  const dot = dir.x * normal.x + dir.y * normal.y;
  return {
    x: dir.x - 2 * dot * normal.x,
    y: dir.y - 2 * dot * normal.y,
  };
}
